import { useEffect, useState, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { Loader2, Receipt, Search, Download, FileText, ExternalLink } from 'lucide-react';

const TYPE_STYLE = {
  subscription: 'text-sky-600 border-sky-300',
  bill_credits: 'text-violet-600 border-violet-300',
  ai_credits: 'text-emerald-600 border-emerald-300',
};

export default function BillingInvoicesTab() {
  const [orgs, setOrgs] = useState([]);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);
  const [f, setF] = useState({ org_id: '', invoice_number: '', date_from: '', date_to: '' });

  useEffect(() => {
    api.get('/finance/orgs').then(({ data }) => setOrgs(data?.orgs || [])).catch(() => {});
  }, []);

  const buildParams = (src) => {
    const p = {};
    Object.entries(src).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  };

  const load = useCallback(async (src) => {
    setLoading(true);
    try {
      const { data } = await api.get('/finance/invoices', { params: buildParams(src || f) });
      setRows(data?.invoices || []);
      setTotal(data?.total_value_usd || 0);
      setCount(data?.count || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load invoices');
    } finally {
      setLoading(false);
    }
  }, [f]);

  useEffect(() => { load(); }, []); // initial load

  const setField = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const reset = () => {
    const empty = { org_id: '', invoice_number: '', date_from: '', date_to: '' };
    setF(empty);
    load(empty);
  };

  const exportCsv = async () => {
    try {
      const { data } = await api.get('/finance/invoices/export.csv', { params: buildParams(f), responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = 'billing_invoices.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  const viewInvoice = async (r) => {
    if (r.hosted_invoice_url) { window.open(r.hosted_invoice_url, '_blank', 'noopener'); return; }
    setViewing(r.number);
    try {
      const { data } = await api.get(`/finance/invoice/${r.account_id}/${r.stream}/${r.ref}`);
      const w = window.open('', '_blank');
      if (w) { w.document.open(); w.document.write(data); w.document.close(); }
      else toast.error('Pop-up blocked — allow pop-ups to view the invoice');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not open invoice');
    } finally { setViewing(null); }
  };

  return (
    <div className="max-w-6xl" data-testid="billing-invoices-tab">
      <div className="mb-6">
        <h2 className="text-xl font-black tracking-tighter flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
          <Receipt className="h-5 w-5 text-primary" /> Billing invoices
        </h2>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          Every subscription renewal and credit top-up across all organizations. Filter by org, invoice number or date, then open the printable invoice.
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 p-4 flex flex-wrap items-end gap-3 mb-4" data-testid="billing-invoices-filters">
        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1">Organization</label>
          <select value={f.org_id} onChange={(e) => setField('org_id', e.target.value)} className="rounded-none border border-slate-200 h-9 px-2 text-sm w-56" data-testid="billing-invoices-org">
            <option value="">All organizations</option>
            {orgs.map((o) => <option key={o.account_id} value={o.account_id}>{o.name}</option>)}
          </select>
        </div>
        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1">Invoice #</label>
          <Input value={f.invoice_number} onChange={(e) => setField('invoice_number', e.target.value)} placeholder="BP-00001 / AI-…" className="rounded-none h-9 w-40 text-sm" data-testid="billing-invoices-number" />
        </div>
        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1">From</label>
          <Input type="date" value={f.date_from} onChange={(e) => setField('date_from', e.target.value)} className="rounded-none h-9 w-40 text-sm" data-testid="billing-invoices-from" />
        </div>
        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1">To</label>
          <Input type="date" value={f.date_to} onChange={(e) => setField('date_to', e.target.value)} className="rounded-none h-9 w-40 text-sm" data-testid="billing-invoices-to" />
        </div>
        <Button onClick={load} className="rounded-none h-9" size="sm" data-testid="billing-invoices-apply"><Search className="h-4 w-4 mr-1" />Apply</Button>
        <Button onClick={reset} variant="outline" className="rounded-none h-9" size="sm" data-testid="billing-invoices-reset">Reset</Button>
        <Button onClick={exportCsv} variant="outline" className="rounded-none h-9 ml-auto" size="sm" data-testid="billing-invoices-export"><Download className="h-4 w-4 mr-1" />CSV</Button>
      </div>

      {/* Summary */}
      <div className="flex items-center gap-6 mb-3 text-sm">
        <span className="text-slate-500">Showing <b className="text-slate-900" data-testid="billing-invoices-count">{count}</b> invoice(s)</span>
        <span className="text-slate-500">Total value <b className="text-emerald-600" data-testid="billing-invoices-total">${total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</b></span>
      </div>

      <div className="bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-left">
              <th className="px-4 py-2.5 text-[11px] uppercase tracking-wide text-slate-500">Invoice #</th>
              <th className="px-4 py-2.5 text-[11px] uppercase tracking-wide text-slate-500">Customer</th>
              <th className="px-4 py-2.5 text-[11px] uppercase tracking-wide text-slate-500">Type</th>
              <th className="px-4 py-2.5 text-[11px] uppercase tracking-wide text-slate-500">Date</th>
              <th className="px-4 py-2.5 text-[11px] uppercase tracking-wide text-slate-500 text-right">Amount</th>
              <th className="px-4 py-2.5 text-[11px] uppercase tracking-wide text-slate-500">Top-up by</th>
              <th className="px-4 py-2.5 text-[11px] uppercase tracking-wide text-slate-500 text-right">Invoice</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="py-16 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} className="py-16 text-center text-slate-400" data-testid="billing-invoices-empty">No invoices match these filters.</td></tr>
            ) : rows.map((r, i) => (
              <tr key={`${r.stream}-${r.ref}-${i}`} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`billing-invoice-row-${i}`}>
                <td className="px-4 py-2.5 font-mono text-[13px]">{r.number}</td>
                <td className="px-4 py-2.5">{r.customer || <span className="text-slate-400">—</span>}</td>
                <td className="px-4 py-2.5">
                  <span className={`inline-block border rounded-none px-2 py-0.5 text-[10px] ${TYPE_STYLE[r.stream] || 'text-slate-500 border-slate-300'}`}>{r.type}</span>
                </td>
                <td className="px-4 py-2.5 text-slate-600">{(r.date || '').slice(0, 10)}</td>
                <td className="px-4 py-2.5 text-right font-semibold">${Number(r.amount_usd || 0).toFixed(2)}</td>
                <td className="px-4 py-2.5 text-slate-600">{r.topup_by || <span className="text-slate-400">System</span>}</td>
                <td className="px-4 py-2.5 text-right">
                  <Button onClick={() => viewInvoice(r)} variant="outline" size="sm" className="rounded-none h-8" data-testid={`billing-invoice-view-${i}`}>
                    {viewing === r.number ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (r.hosted_invoice_url ? <><ExternalLink className="h-3.5 w-3.5 mr-1" />Stripe</> : <><FileText className="h-3.5 w-3.5 mr-1" />PDF</>)}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
